<script setup lang="ts">
import { ref, onMounted } from "vue"
import { ImageIcon, MessageCircle } from "lucide-vue-next"

import AttachmentPreview from "@/components/ui-custom/attachment-preview/AttachmentPreview.vue"
import ChatPanel from "@/components/ui-custom/chat-panel/ChatPanel.vue"

import type { Attachment } from "@/models/article.ts"

interface Props {
  slides?: Attachment[]
  chatToggleable?: boolean
  selectedFile: Object
}
withDefaults(defineProps<Props>(), {
  slides: () => [],
  chatToggleable: false
})

const chatEnabled = ref(true)
const typewriterComplete = ref(false)

const toggleChat = (val: boolean) => {
  chatEnabled.value = val
}

const chatOptions = {
  datasourceName: "articles",
  path: "@pubblication_year/@pubblication_number"
}

onMounted(() => {
  const typewriterDuration = 3000

  setTimeout(() => {
    typewriterComplete.value = true
  }, typewriterDuration)
})
</script>

<template>
  <div class="flex w-full flex-col">
    <div class="flex flex-row items-center bg-gray-400">
      <div class="custom-tabs" :class="{ active: chatEnabled }" @click="toggleChat(true)">
        <image-icon class="mr-2" /> {{ $t("commons.zapdoc.preview") }}
      </div>

      <div
        class="custom-tabs flex items-center justify-between"
        :class="{ active: !chatEnabled }"
        @click="toggleChat(false)">
        <span class="flex items-center">
          <message-circle class="mr-2" :class="{ 'custom-pulse': !typewriterComplete }" />
          <span
            class="before:animate-typewriter relative w-[max-content] before:absolute before:inset-0 before:bg-primary"
            :class="{
              'before:!bg-gray-400': chatEnabled,
              'after:animate-caret after:absolute after:inset-0 after:w-[0.125em] after:bg-white': !typewriterComplete
            }">
            {{ $t("commons.zapdoc.chat") }}
          </span>
        </span>
      </div>
    </div>

    <div class="relative flex-1 text-black">
      <AttachmentPreview :slides="slides" :chat-toggleable="true" v-if="slides && chatEnabled" />

      <chat-panel
        :datasource-name="chatOptions.datasourceName"
        :path="chatOptions.path"
        :is-folder="false"
        :selected-file="selectedFile"
        v-if="!chatEnabled" />
    </div>
  </div>
</template>

<style scoped>
@keyframes customPulse {
  0% {
    opacity: 1;
    color: hsl(var(--primary-forground));
  }
  50% {
    opacity: 0.4;
    color: hsl(var(--primary));
  }
  100% {
    opacity: 1;
    color: hsl(var(--primary-forground));
  }
}

.custom-pulse {
  animation: customPulse 1.5s infinite ease-in-out;
}
</style>
